import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Edit2, Trash2, Search, X, Shield, CheckSquare, Square } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const UserTypeMenuAccess = () => {
  const [accessList,         setAccessList]         = useState([]);
  const [userTypes,          setUserTypes]          = useState([]);
  const [menuItems,          setMenuItems]          = useState([]);
  const [filteredAccessList, setFilteredAccessList] = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [searchTerm,         setSearchTerm]         = useState('');
  const [filterUserTypeId,   setFilterUserTypeId]   = useState('');
  const [showModal,          setShowModal]          = useState(false);
  const [editMode,           setEditMode]           = useState(false);
  const [submitLoading,      setSubmitLoading]      = useState(false);
  const [errors,             setErrors]             = useState({});

  const [formData, setFormData] = useState({
    accessid: null, usertypeid: '', menuid: '',
    canview: true, canadd: false, canedit: false, candelete: false,
  });

  useEffect(() => { fetchAccessList(); fetchUserTypes(); fetchMenuItems(); }, []);
  useEffect(() => { filterAccess(); }, [searchTerm, filterUserTypeId, accessList]);

  const fetchAccessList = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/UserTypeMenuAccess.php');
      if (res.data.success) setAccessList(res.data.data || []);
    } catch (err) { if (err.response?.status === 401) alert('Session expired. Please login again.'); }
    finally { setLoading(false); }
  };

  const fetchUserTypes = async () => {
    try { const res = await apiClient.get('/UserTypes.php', { params: { active: true } }); if (res.data.success) setUserTypes(res.data.data || []); } catch (e) { console.error(e); }
  };

  const fetchMenuItems = async () => {
    try { const res = await apiClient.get('/MenuItems.php', { params: { active: true } }); if (res.data.success) setMenuItems(res.data.data || []); } catch (e) { console.error(e); }
  };

  const filterAccess = () => {
    let f = accessList;
    if (filterUserTypeId) f = f.filter(a => a.usertypeid === parseInt(filterUserTypeId));
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      f = f.filter(a =>
        a.usertypename.toLowerCase().includes(q) ||
        a.menuname.toLowerCase().includes(q) ||
        a.menucode.toLowerCase().includes(q)
      );
    }
    setFilteredAccessList(f);
  };

  const getAvailableMenus = useCallback((userTypeId) => {
    if (!userTypeId) return menuItems;
    const assigned = new Set(accessList.filter(a => parseInt(a.usertypeid) === parseInt(userTypeId)).map(a => parseInt(a.menuid)));
    return menuItems.filter(m => !assigned.has(parseInt(m.menuid)));
  }, [menuItems, accessList]);

  const availableMenus = useMemo(() => {
    if (editMode) return menuItems;
    return getAvailableMenus(formData.usertypeid);
  }, [editMode, formData.usertypeid, menuItems, getAvailableMenus]);

  const validateForm = () => {
    const errs = {};
    if (!formData.usertypeid) errs.usertypeid = 'User type is required';
    if (!formData.menuid)     errs.menuid     = 'Menu is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const res = await apiClient[method]('/UserTypeMenuAccess.php', { ...formData, usertypeid: parseInt(formData.usertypeid), menuid: parseInt(formData.menuid), canview: formData.canview ? 1 : 0, canadd: formData.canadd ? 1 : 0, canedit: formData.canedit ? 1 : 0, candelete: formData.candelete ? 1 : 0 });
      if (res.data.success) {
        alert(editMode ? 'Menu access updated successfully!' : 'Menu access created successfully!');
        closeModal(); fetchAccessList();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401)      alert('Session expired. Please login again.');
      else if (err.response?.status === 409) alert('Menu access already exists for this user type.');
      else                                   alert('Error saving menu access. Please try again.');
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (access) => {
    setFormData({ accessid: access.accessid, usertypeid: access.usertypeid, menuid: access.menuid, canview: access.canview === 1, canadd: access.canadd === 1, canedit: access.canedit === 1, candelete: access.candelete === 1 });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this menu access?')) return;
    try {
      const res = await apiClient.delete('/UserTypeMenuAccess.php', { data: { accessid: id } });
      if (res.data.success) { alert('Menu access removed successfully!'); fetchAccessList(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error removing menu access. Please try again.');
    }
  };

  const handleUserTypeChange = (e) => setFormData({ ...formData, usertypeid: e.target.value, menuid: '' });

  const resetForm    = () => { setFormData({ accessid: null, usertypeid: '', menuid: '', canview: true, canadd: false, canedit: false, candelete: false }); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const PermissionIcon = ({ allowed }) =>
    allowed
      ? <CheckSquare size={17} className="text-emerald-500 mx-auto" />
      : <Square      size={17} className="text-gray-300 mx-auto" />;

  const selectCls = (err, disabled) =>
    `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200
    focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]
    ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}
    ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;

  const labelCls = 'block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide';
  const errMsg   = (msg) => msg ? <p className="text-xs text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

  const hasFilters = searchTerm || filterUserTypeId;

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Shield size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>User Type Menu Access</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage menu permissions for user types</p>
          </div>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Permission
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by user type or menu name..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>

        <select value={filterUserTypeId} onChange={e => setFilterUserTypeId(e.target.value)}
          className="px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-700 bg-white outline-none cursor-pointer transition-all duration-150 hover:border-[#0d1c6b] focus:border-[#0d1c6b]">
          <option value="">All User Types</option>
          {userTypes.map(t => <option key={t.usertypeid} value={t.usertypeid}>{t.usertypename}</option>)}
        </select>

        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredAccessList.length} {filteredAccessList.length === 1 ? 'permission' : 'permissions'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading menu access permissions...</div>
        ) : filteredAccessList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <Shield size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No menu access permissions found</p>
            {hasFilters && (
              <button onClick={() => { setSearchTerm(''); setFilterUserTypeId(''); }}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear filters</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['User Type', 'Menu', 'View', 'Add', 'Edit', 'Delete', 'Created At', 'Actions'].map((h, i) => (
                    <th key={h} className={`px-4 py-3.5 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0 ${i >= 2 && i <= 5 ? 'text-center' : 'text-left'}`}
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAccessList.map(access => (
                  <tr key={access.accessid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <span className="inline-block px-2.5 py-1 bg-violet-100 text-violet-700 border-2 border-violet-200 rounded-lg text-[11px] font-bold">
                        {access.usertypename}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-gray-800">
                      {access.menucode} — {access.menuname}
                    </td>
                    <td className="px-4 py-3.5 text-center"><PermissionIcon allowed={access.canview   === 1} /></td>
                    <td className="px-4 py-3.5 text-center"><PermissionIcon allowed={access.canadd    === 1} /></td>
                    <td className="px-4 py-3.5 text-center"><PermissionIcon allowed={access.canedit   === 1} /></td>
                    <td className="px-4 py-3.5 text-center"><PermissionIcon allowed={access.candelete === 1} /></td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">{new Date(access.createdat).toLocaleDateString()}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(access)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(access.accessid)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[540px] shadow-2xl border border-gray-200 overflow-hidden"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <Shield size={18} /> {editMode ? 'Edit Menu Permission' : 'Add Menu Permission'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-7">

              <div className="grid grid-cols-2 gap-4 mb-5">
                {/* User Type */}
                <div>
                  <label className={labelCls}>User Type <span className="text-red-500">*</span></label>
                  <select value={formData.usertypeid} onChange={handleUserTypeChange} disabled={editMode}
                    className={selectCls(errors.usertypeid, editMode)}>
                    <option value="">-- Select User Type --</option>
                    {userTypes.map(t => <option key={t.usertypeid} value={t.usertypeid}>{t.usertypename}</option>)}
                  </select>
                  {errMsg(errors.usertypeid)}
                </div>

                {/* Menu */}
                <div>
                  <label className={labelCls}>Menu <span className="text-red-500">*</span></label>
                  <select value={formData.menuid}
                    onChange={e => setFormData({ ...formData, menuid: e.target.value })}
                    disabled={editMode || !formData.usertypeid}
                    className={selectCls(errors.menuid, editMode || !formData.usertypeid)}>
                    <option value="">
                      {!formData.usertypeid
                        ? '-- Select a User Type first --'
                        : availableMenus.length === 0
                          ? '-- All menus already assigned --'
                          : '-- Select Menu --'}
                    </option>
                    {availableMenus.map(m => <option key={m.menuid} value={m.menuid}>{m.menucode} — {m.menuname}</option>)}
                  </select>
                  {errMsg(errors.menuid)}
                  {!editMode && formData.usertypeid && (
                    <p className="text-[11px] text-gray-400 mt-1">
                      {availableMenus.length === 0
                        ? 'All menus have been assigned to this user type.'
                        : `${availableMenus.length} unassigned menu${availableMenus.length !== 1 ? 's' : ''} available`}
                    </p>
                  )}
                </div>
              </div>

              {/* Permissions */}
              <div className="mb-6">
                <label className={labelCls}>Permissions</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'canview',   label: 'Can View'   },
                    { key: 'canadd',    label: 'Can Add'    },
                    { key: 'canedit',   label: 'Can Edit'   },
                    { key: 'candelete', label: 'Can Delete' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-3 cursor-pointer px-4 py-2.5 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                      <input type="checkbox" checked={formData[key]}
                        onChange={e => setFormData({ ...formData, [key]: e.target.checked })}
                        className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                      <span className="text-sm font-semibold text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-2">Select which operations this user type can perform on this menu</p>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit"
                  disabled={submitLoading || (!editMode && availableMenus.length === 0 && !!formData.usertypeid)}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : editMode ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default UserTypeMenuAccess;